import React from "react";
import { motion } from "framer-motion";
import { Brain, Target, Rocket, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { 
  cn, 
  scrollToSection 
} from "@/lib";
import { 
  services, 
  testimonials, 
  portfolioItems, 
  whyChooseUs 
} from "@/data";
import { 
  ServiceCard, 
  TestimonialCard, 
  PortfolioCard 
} from "@/components/Cards";
import { AnimatedBackground } from "@/components/AnimatedBackground";
import { SocialConnect } from "@/components/SocialConnect";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: { once: true }
};

const iconMap: Record<string, React.ReactNode> = {
  Brain: <Brain className="w-6 h-6" />,
  Target: <Target className="w-6 h-6" />,
  Rocket: <Rocket className="w-6 h-6" />,
  ShieldCheck: <ShieldCheck className="w-6 h-6" />
};

export default function Home() {
  return (
    <div className="relative w-full overflow-hidden">
      {/* 1️⃣ Hero Section */}
      <section 
        id="hero" 
        className="relative min-h-screen flex items-center justify-center pt-20"
      >
        <AnimatedBackground className="absolute inset-0 z-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/40 to-background z-10" />
        
        <div className="container mx-auto px-4 relative z-20 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              Next-Gen AI Agency
            </span>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground via-primary to-accent">
              Automate Your Business. <br className="hidden md:block" />
              Dominate Online.
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              We build AI-powered systems & high-converting websites that grow your business automatically. 
              Experience the fusion of Silicon Valley tech and luxury brand positioning.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="h-14 px-8 rounded-full bg-primary text-primary-foreground hover:shadow-[0_0_30px_rgba(var(--primary),0.4)] transition-all duration-300 group text-lg"
                onClick={() => scrollToSection("services")}
              >
                🚀 Get Started
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="h-14 px-8 rounded-full border-primary/20 backdrop-blur-md hover:bg-primary/5 transition-all duration-300 text-lg"
                onClick={() => scrollToSection("social-connect")}
              >
                📩 Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2️⃣ Why Choose Xuvera */}
      <section id="why-choose" className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Why Choose Xuvera?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">The intersection of artificial intelligence and premium design.</p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {whyChooseUs.map((item) => (
              <motion.div
                key={item.id}
                variants={fadeInUp}
                className="group relative p-8 rounded-3xl border border-border bg-card/50 backdrop-blur-xl hover:border-primary/50 hover:shadow-[0_10px_40px_-10px_rgba(var(--primary),0.2)] transition-all duration-500 overflow-hidden"
              >
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:scale-110 transition-transform">
                  {iconMap[item.icon]}
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3️⃣ Services Section */}
      <section id="services" className="py-24 bg-muted/30 relative">
        <div className="container mx-auto px-4">
          <motion.div 
            className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
            {...fadeInUp}
          >
            <div>
              <h2 className="text-3xl md:text-5xl font-bold mb-4">Elite AI Services</h2>
              <p className="text-muted-foreground max-w-lg">Propel your business into the future with our specialized AI solutions.</p>
            </div>
            <Button variant="link" className="text-primary p-0 h-auto text-lg group">
              View all services <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* 4️⃣ Portfolio Section */}
      <section id="portfolio" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Our Masterpieces</h2>
            <p className="text-muted-foreground">A showcase of high-performance digital products engineered by Xuvera.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioItems.map((item) => (
              <PortfolioCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* 5️⃣ Social Connect Section */}
      <section id="social-connect" className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-4xl bg-accent/10 blur-[120px] rounded-full z-0" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Connect With Us</h2>
            <p className="text-muted-foreground">Join our digital ecosystem across platforms.</p>
          </motion.div>
          
          <SocialConnect />
        </div>
      </section>

      {/* 6️⃣ Testimonials Section */}
      <section id="testimonials" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Client Feedback</h2>
            <p className="text-muted-foreground">Trusted by forward-thinking leaders worldwide.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* 7️⃣ CTA Section */}
      <section id="cta" className="py-24 relative">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-[3rem] p-12 md:p-24 text-center border border-primary/20 bg-gradient-to-br from-card via-card to-primary/5">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5 pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-accent/20 blur-[100px] rounded-full" />
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/20 blur-[100px] rounded-full" />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative z-10"
            >
              <h2 className="text-4xl md:text-6xl font-extrabold mb-8 tracking-tight">
                Ready to Scale Your <br />
                Business With AI?
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
                Stop doing repetitive work. Start building the future. Our team is ready to engineer your automated empire.
              </p>
              <Button 
                size="lg" 
                className="h-16 px-12 rounded-full bg-primary text-primary-foreground hover:scale-105 transition-all duration-300 text-xl font-bold shadow-xl shadow-primary/25 group"
                onClick={() => window.open("mailto:xuvera122@gmail.com", "_blank")}
              >
                🚀 Work With Xuvera
                <ArrowRight className="ml-3 w-6 h-6 transition-transform group-hover:translate-x-1" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
